import {
  Activity,
  Backpack,
  BadgeCheck,
  Ban,
  Banknote,
  BarChart3,
  Bird,
  BookLock,
  BookOpen,
  Bot,
  Boxes,
  BriefcaseBusiness,
  Bug,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  Camera,
  Car,
  Castle,
  Cctv,
  CircleDot,
  ClipboardCheck,
  CloudSun,
  Crosshair,
  Crown,
  CupSoda,
  DoorClosed,
  DoorOpen,
  Droplets,
  Dumbbell,
  FileBadge,
  FileCheck2,
  FileText,
  Flag,
  FlaskConical,
  Fuel,
  Gauge,
  Gem,
  Gift,
  Globe,
  Hammer,
  HandCoins,
  Headset,
  HeartPulse,
  House,
  IdCard,
  Images,
  Inbox,
  KanbanSquare,
  KeyRound,
  Landmark,
  LibraryBig,
  LayoutGrid,
  ListChecks,
  LockKeyhole,
  Mail,
  MapPinned,
  MessageSquareWarning,
  MessagesSquare,
  Monitor,
  MonitorCheck,
  Network,
  Newspaper,
  PersonStanding,
  PhoneCall,
  Radio,
  RadioTower,
  RectangleHorizontal,
  Receipt,
  Scale,
  ScrollText,
  Server,
  ServerCog,
  Settings,
  ShieldCheck,
  Shirt,
  ShoppingBasket,
  Siren,
  SlidersHorizontal,
  Smartphone,
  Sofa,
  Sparkles,
  SprayCan,
  Sprout,
  Megaphone,
  Stethoscope,
  Store,
  Tablet,
  Tags,
  Target,
  TrafficCone,
  Trophy,
  Truck,
  type LucideIcon,
  Usb,
  Vault,
  Users,
  Volleyball,
  Volume2,
  Vote,
  Wallet,
  Warehouse,
  Wrench,
  Zap,
} from 'lucide-react'

import { Permission, type PermissionKey } from '@/lib/permissions'

export interface NavItem {
  id: string
  label: string
  to: string
  icon: LucideIcon
  /** Any one of these grants access to the item. */
  permissions: PermissionKey[]
  /** Live counter key, filled in from the dashboard summary. */
  badgeKey?: 'openTickets' | 'pendingModeration' | 'whitelist'
  children?: NavItem[]
  end?: boolean
}

export interface NavGroup {
  /** Keys the group's open or closed state, remembered across visits. */
  id: string
  /** A group without one is always open, above the others. */
  heading?: string
  items: NavItem[]
}

export const NAVIGATION: NavGroup[] = [
  {
    id: 'home',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        to: '/',
        icon: Gauge,
        permissions: [Permission.DASHBOARD_VIEW],
        end: true,
      },
    ],
  },
  {
    // The team's own work: what players ask of it, how it keeps order, and who is on it.
    id: 'staff',
    heading: 'Staff & support',
    items: [
      {
        id: 'tickets',
        label: 'Tickets',
        to: '/tickets',
        icon: Inbox,
        permissions: [Permission.TICKETS_VIEW, Permission.TICKETS_VIEW_ALL],
        badgeKey: 'openTickets',
        end: true,
      },
      {
        id: 'bugs',
        label: 'Bug tracker',
        to: '/bugs',
        icon: Bug,
        permissions: [Permission.BUGS_VIEW],
      },
      {
        id: 'roadmap',
        label: 'Roadmap',
        to: '/roadmap',
        icon: KanbanSquare,
        permissions: [Permission.ROADMAP_VIEW],
      },
      {
        // Read by everybody who may open the console: the rules the team works by.
        id: 'staff-rulebook',
        label: 'Staff rulebook',
        to: '/staff-rules',
        icon: BookLock,
        permissions: [Permission.PANEL_ACCESS],
      },
      {
        id: 'status-page',
        label: 'Status page',
        to: '/status-page',
        icon: MonitorCheck,
        permissions: [Permission.STATUS_PAGE_VIEW],
      },
      {
        id: 'beta-test',
        label: 'Beta test',
        to: '/beta-test',
        icon: ClipboardCheck,
        permissions: [Permission.TESTERS_VIEW],
      },
      {
        id: 'wiki',
        label: 'Wiki',
        to: '/wiki',
        icon: LibraryBig,
        permissions: [Permission.WIKI_EDIT, Permission.WIKI_MANAGE],
      },
      {
        id: 'moderation',
        label: 'Moderation',
        to: '/moderation',
        icon: ShieldCheck,
        permissions: [Permission.MODERATION_VIEW],
        badgeKey: 'pendingModeration',
        end: true,
      },
      {
        id: 'discipline',
        label: 'Discipline',
        to: '/discipline',
        icon: Scale,
        permissions: [Permission.DISCIPLINE_VIEW],
        children: [
          {
            id: 'discipline-register',
            label: 'Register',
            to: '/discipline',
            icon: ScrollText,
            permissions: [Permission.DISCIPLINE_VIEW],
            end: true,
          },
          {
            id: 'discipline-sanctions',
            label: 'Sanctions & bans',
            to: '/discipline/sanctions',
            icon: Ban,
            permissions: [Permission.DISCIPLINE_VIEW],
          },
          {
            id: 'discipline-appeals',
            label: 'Appeals',
            to: '/discipline/appeals',
            icon: Scale,
            permissions: [Permission.DISCIPLINE_VIEW],
          },
        ],
      },
      {
        id: 'applications',
        label: 'Applications',
        to: '/applications',
        icon: BriefcaseBusiness,
        permissions: [Permission.APPLICATIONS_VIEW],
        end: true,
      },
      {
        id: 'members',
        label: 'Members',
        to: '/members',
        icon: Users,
        permissions: [Permission.MEMBERS_VIEW],
        end: true,
      },
      {
        id: 'absences',
        label: 'Absences',
        to: '/absences',
        icon: CalendarDays,
        permissions: [Permission.ABSENCES_VIEW],
        end: true,
      },
    ],
  },
  {
    // What the bot does in the Discord server for the whole community.
    id: 'discord',
    heading: 'Discord',
    items: [
      {
        // Channels, categories, roles and permissions, asked in words and previewed.
        id: 'discord-ai',
        label: 'AI architect',
        to: '/discord-ai',
        icon: Sparkles,
        permissions: [Permission.BOT_ARCHITECT],
        end: true,
      },
      {
        id: 'giveaways',
        label: 'Giveaways',
        to: '/giveaways',
        icon: Gift,
        permissions: [Permission.GIVEAWAYS_VIEW],
        end: true,
      },
      {
        id: 'polls',
        label: 'Polls',
        to: '/polls',
        icon: Vote,
        permissions: [Permission.POLLS_VIEW],
        end: true,
      },
      {
        id: 'messages',
        label: 'Scheduled messages',
        to: '/messages',
        icon: CalendarClock,
        permissions: [Permission.MESSAGES_VIEW],
        end: true,
      },
      {
        id: 'voice',
        label: 'Voice channels',
        to: '/voice',
        icon: Volume2,
        permissions: [Permission.VOICE_VIEW],
        children: [
          {
            id: 'voice-floating',
            label: 'Floating channels',
            to: '/voice',
            icon: Volume2,
            permissions: [Permission.VOICE_VIEW],
            end: true,
          },
          {
            id: 'voice-stats',
            label: 'Stats channels',
            to: '/voice/stats',
            icon: BarChart3,
            permissions: [Permission.VOICE_VIEW],
          },
        ],
      },
    ],
  },
  {
    // Who may play and what they are in game. Candidature stays under staff, which is the
    // line: the whitelist decides who may play, a candidature decides who joins the team.
    id: 'players',
    heading: 'Players',
    items: [
      {
        id: 'rulebook',
        label: 'Rulebook',
        to: '/rules',
        icon: BookOpen,
        permissions: [Permission.RULES_VIEW],
        end: true,
      },
      {
        id: 'whitelist',
        label: 'Whitelist',
        to: '/whitelist',
        icon: BadgeCheck,
        permissions: [Permission.WHITELIST_VIEW],
        badgeKey: 'whitelist',
        end: true,
      },
      {
        id: 'characters',
        label: 'Characters',
        to: '/characters',
        icon: IdCard,
        permissions: [Permission.CHARACTERS_VIEW],
        end: true,
      },
      {
        id: 'inventory',
        label: 'Inventory',
        to: '/inventory',
        icon: Backpack,
        permissions: [Permission.INVENTORY_VIEW],
        end: true,
      },
      {
        // An item says which game weapon it is; this is where that weapon's every number
        // is tuned.
        id: 'weapons',
        label: 'Weapons',
        to: '/weapons',
        icon: Crosshair,
        permissions: [Permission.WEAPONS_VIEW],
      },
      {
        // How hungry, thirsty and stressed a character gets.
        id: 'status',
        label: 'Status',
        to: '/status',
        icon: HeartPulse,
        permissions: [Permission.STATUS_VIEW],
        end: true,
      },
      {
        // How good a character is at driving, running, shooting..., and what it changes.
        id: 'skills',
        label: 'Skills',
        to: '/skills',
        icon: Dumbbell,
        permissions: [Permission.SKILLS_VIEW],
        end: true,
      },
      {
        // The talent tree players spend their points on, and how they earn them.
        id: 'talents',
        label: 'Talents',
        to: '/talents',
        icon: Network,
        permissions: [Permission.TALENTS_VIEW],
        end: true,
      },
      {
        // Every emote the game declares, its preview photographed in game with /emotefoto,
        // and how many characters keep it among their favourites.
        id: 'emotes',
        label: 'Emotes',
        to: '/emotes',
        icon: PersonStanding,
        permissions: [Permission.EMOTES_VIEW],
        end: true,
      },
      {
        // The city's sky and clock: change them now, and how they change by themselves.
        id: 'weather',
        label: 'Weather',
        to: '/weather',
        icon: CloudSun,
        permissions: [Permission.WEATHER_VIEW],
        end: true,
      },
      {
        // Ambient traffic and people by hour, how drivers drive, rush-hour jams, and
        // passers-by who call the 118 and the 911.
        id: 'npc',
        label: 'NPCs & traffic',
        to: '/npc',
        icon: TrafficCone,
        permissions: [Permission.NPC_VIEW],
        end: true,
      },
      {
        // The hospital's files on every character, who is hurt right now, and how the
        // body bleeds, heals and is billed.
        id: 'medical',
        label: 'Medical',
        to: '/medical',
        icon: Stethoscope,
        permissions: [Permission.MEDICAL_VIEW],
      },
      {
        // What the city asks of a character, and what it pays.
        id: 'quests',
        label: 'Quests',
        to: '/quests',
        icon: Target,
        permissions: [Permission.QUESTS_VIEW],
        children: [
          {
            id: 'quests-catalogue',
            label: 'Quests',
            to: '/quests',
            icon: Target,
            permissions: [Permission.QUESTS_VIEW],
            end: true,
          },
          {
            id: 'quests-progress',
            label: 'Progress',
            to: '/quests/progress',
            icon: ListChecks,
            permissions: [Permission.QUESTS_VIEW],
          },
        ],
      },
      {
        // Seasons of tiers a character climbs with XP, and the premium passes sold on
        // Tebex that open each season's second track.
        id: 'battlepass',
        label: 'Battle pass',
        to: '/battlepass',
        icon: Trophy,
        permissions: [Permission.BATTLEPASS_VIEW],
        children: [
          {
            id: 'battlepass-seasons',
            label: 'Seasons',
            to: '/battlepass',
            icon: Trophy,
            permissions: [Permission.BATTLEPASS_VIEW],
            end: true,
          },
          {
            id: 'battlepass-passes',
            label: 'Premium passes',
            to: '/battlepass/passes',
            icon: Crown,
            permissions: [Permission.BATTLEPASS_VIEW],
          },
          {
            id: 'battlepass-payments',
            label: 'Tebex purchases',
            to: '/battlepass/payments',
            icon: Receipt,
            permissions: [Permission.BATTLEPASS_VIEW],
          },
        ],
      },
    ],
  },
  {
    // The running game as a place: who is where, what is said, and what staff set down on
    // the map.
    id: 'city',
    heading: 'City',
    items: [
      {
        // Where everybody is, and where they were when something was said.
        id: 'live-map',
        label: 'Live map',
        to: '/live-map',
        icon: MapPinned,
        permissions: [Permission.LIVE_MAP_VIEW],
        end: true,
      },
      {
        id: 'game-chat',
        label: 'Game chat',
        to: '/chat',
        icon: MessagesSquare,
        permissions: [Permission.CHAT_VIEW],
        end: true,
      },
      {
        // The locks on the map's doors and gates, set down in game with /porte, and who
        // opens each: a business's people, a character, a key or a card.
        id: 'doors',
        label: 'Doors',
        to: '/doors',
        icon: DoorClosed,
        permissions: [Permission.DOORS_VIEW],
      },
      {
        // Where staff hung the cameras in game and what each looks at. What they see is
        // watched in game, never here.
        id: 'cctv',
        label: 'Security cameras',
        to: '/cctv',
        icon: Cctv,
        permissions: [Permission.CCTV_VIEW],
      },
      {
        // The tags players spray on the walls, as they are painted, and staff's power to
        // take them off.
        id: 'graffiti',
        label: 'Graffiti',
        to: '/graffiti',
        icon: SprayCan,
        permissions: [Permission.GRAFFITI_VIEW],
      },
      {
        // The police's portable LED message boards standing in the city, what each
        // says, and staff's power to take one away.
        id: 'ledpanels',
        label: 'LED boards',
        to: '/ledpanels',
        icon: Megaphone,
        permissions: [Permission.LEDPANELS_VIEW],
      },
      {
        // The folding ping pong tables players set down in the city, the latest
        // matches and the leaderboard, and staff's power to take tables away.
        id: 'pingpong',
        label: 'Ping pong',
        to: '/pingpong',
        icon: Volleyball,
        permissions: [Permission.PINGPONG_VIEW],
      },
      {
        // The drug plants growing in the city, the chemistry tables and drying
        // benches, the log, and staff's power to remove them or start a wipe.
        id: 'drugs',
        label: 'Drugs',
        to: '/drugs',
        icon: FlaskConical,
        permissions: [Permission.DRUGS_VIEW],
      },
      {
        // Underground races from the Nitro app: races with their entrants and payouts,
        // the tracks players drew, the racers by rating, and official races staff schedule.
        id: 'races',
        label: 'Races',
        to: '/races',
        icon: Flag,
        permissions: [Permission.RACES_VIEW],
      },
      {
        // VOLTA's shared e-scooters: the fleet and its stations on the map, the rides and
        // what riders owe, the chargers, the fares and rules.
        id: 'scooters',
        label: 'E-scooters',
        to: '/scooters',
        icon: Zap,
        permissions: [Permission.SCOOTERS_VIEW],
      },
      {
        // The map's vending machines players have used, how many are left in each, and
        // staff's power to send a delivery ahead of the twelve hours.
        id: 'vending',
        label: 'Vending machines',
        to: '/vending',
        icon: CupSoda,
        permissions: [Permission.VENDING_VIEW],
      },
      {
        // The 24/7s, LTDs and liquor stores businesses buy and run: their tills, stock
        // and orders, and the terms owners work by.
        id: 'shops',
        label: 'Convenience stores',
        to: '/shops',
        icon: ShoppingBasket,
        permissions: [Permission.SHOPS_VIEW],
      },
      {
        // The pawn shops businesses buy and run, what they trade within which bounds, and
        // the register of every trade the police read in the MDT.
        id: 'pawn',
        label: 'Pawn shops',
        to: '/pawn',
        icon: Gem,
        permissions: [Permission.PAWN_VIEW],
      },
      {
        // Clothing stores, barbers, tattoo parlours and the hospital's plastic surgeon:
        // the places, each chain's catalogue, and the prices.
        id: 'style',
        label: 'Style',
        to: '/style',
        icon: Shirt,
        permissions: [Permission.STYLE_VIEW],
      },
      {
        // The recipes of every trade, the public workbenches, the log of what was made
        // and bought, and each character's mastery and blueprints.
        id: 'crafting',
        label: 'Crafting',
        to: '/crafting',
        icon: Hammer,
        permissions: [Permission.CRAFTING_VIEW],
      },
      {
        // The chess tables staff put down in the city, every game played at them
        // (replayed move by move) and the Elo ladder.
        id: 'chess',
        label: 'Chess',
        to: '/chess',
        icon: Castle,
        permissions: [Permission.CHESS_VIEW],
      },
      {
        // The pool tables staff put down in the city (the map's own three are always
        // there), every eight-ball and nine-ball game played between two people and the
        // Elo ladder.
        id: 'billiards',
        label: 'Billiards',
        to: '/billiards',
        icon: CircleDot,
        permissions: [Permission.BILLIARDS_VIEW],
      },
      {
        // The desktop PCs standing in the world, whose they are and whether they are on,
        // and staff's power to switch one off or take a piece away.
        id: 'desktops',
        label: 'Desktop PCs',
        to: '/desktops',
        icon: Monitor,
        permissions: [Permission.COMPUTERS_VIEW],
      },
      {
        // The accounts organisations' members sign into on their computers (the LSPD's
        // Active Directory), and staff's power to reset, revoke, disable or sign one out.
        id: 'directory',
        label: 'Directory',
        to: '/directory',
        icon: ShieldCheck,
        permissions: [Permission.DIRECTORY_VIEW],
      },
      {
        // The racks players stand in the city, what is mounted in each and how hot it
        // runs, and staff's power to switch a device off or take one away.
        id: 'datacenter',
        label: 'Server rooms',
        to: '/datacenter',
        icon: ServerCog,
        permissions: [Permission.DATACENTER_VIEW],
      },
      {
        // The shipping containers players turned into hydroponic farms, their racks,
        // water and power, the harvests and the tanks on pickups, and staff's power
        // to remove a container or set its level.
        id: 'grow',
        label: 'Grow containers',
        to: '/grow',
        icon: Sprout,
        permissions: [Permission.GROW_VIEW],
      },
    ],
  },
  {
    // Money and what it buys.
    id: 'economy',
    heading: 'Economy',
    items: [
      {
        // Cash is an item, a balance is not: the accounts, loans and skimmers live here.
        id: 'banking',
        label: 'Banking',
        to: '/banking',
        icon: Landmark,
        permissions: [Permission.BANKING_VIEW],
        children: [
          {
            id: 'banking-overview',
            label: 'Overview',
            to: '/banking',
            icon: Banknote,
            permissions: [Permission.BANKING_VIEW],
            end: true,
          },
          {
            id: 'banking-accounts',
            label: 'Accounts',
            to: '/banking/accounts',
            icon: Wallet,
            permissions: [Permission.BANKING_VIEW],
          },
          {
            id: 'banking-ledger',
            label: 'Ledger',
            to: '/banking/ledger',
            icon: Receipt,
            permissions: [Permission.BANKING_VIEW],
          },
          {
            id: 'banking-loans',
            label: 'Loans',
            to: '/banking/loans',
            icon: HandCoins,
            permissions: [Permission.BANKING_VIEW],
          },
          {
            id: 'banking-skimmers',
            label: 'Skimmers',
            to: '/banking/skimmers',
            icon: Usb,
            permissions: [Permission.BANKING_VIEW],
          },
          {
            id: 'banking-labels',
            label: 'Labels',
            to: '/banking/labels',
            icon: Tags,
            permissions: [Permission.BANKING_MANAGE],
          },
          {
            id: 'banking-setup',
            label: 'Setup',
            to: '/banking/setup',
            icon: Wrench,
            permissions: [Permission.BANKING_MANAGE],
          },
        ],
      },
      {
        // Where most of the money is earned: every wage and bill lands in the accounts
        // above.
        id: 'businesses',
        label: 'Businesses',
        to: '/businesses',
        icon: Store,
        permissions: [Permission.BUSINESSES_VIEW],
        children: [
          {
            id: 'businesses-list',
            label: 'Businesses',
            to: '/businesses',
            icon: Store,
            permissions: [Permission.BUSINESSES_VIEW],
            end: true,
          },
          {
            id: 'businesses-registrations',
            label: 'Requests',
            to: '/businesses/registrations',
            icon: FileCheck2,
            permissions: [Permission.BUSINESSES_VIEW],
          },
        ],
      },
      {
        // Properties, owned by characters and businesses alike.
        id: 'housing',
        label: 'Housing',
        to: '/housing',
        icon: House,
        permissions: [Permission.HOUSING_VIEW, Permission.SHELLS_VIEW],
        children: [
          {
            id: 'housing-properties',
            label: 'Properties',
            to: '/housing',
            icon: House,
            permissions: [Permission.HOUSING_VIEW],
            end: true,
          },
          {
            id: 'housing-furniture',
            label: 'Furniture',
            to: '/housing/furniture',
            icon: Sofa,
            permissions: [Permission.HOUSING_VIEW],
          },
          {
            id: 'housing-interiors',
            label: 'Shells & interiors',
            to: '/housing/interiors',
            icon: DoorOpen,
            permissions: [Permission.HOUSING_VIEW],
          },
          {
            // Interiors drawn in game with the shell creator.
            id: 'housing-shells',
            label: 'Shell designs',
            to: '/housing/shells',
            icon: Boxes,
            permissions: [Permission.SHELLS_VIEW],
          },
          {
            id: 'housing-settings',
            label: 'Settings',
            to: '/housing/settings',
            icon: SlidersHorizontal,
            permissions: [Permission.HOUSING_VIEW],
          },
        ],
      },
      {
        id: 'vehicles',
        label: 'Vehicles',
        to: '/vehicles',
        icon: Car,
        permissions: [
          Permission.VEHICLES_VIEW,
          Permission.VEHICLES_MANAGE,
          Permission.VEHICLES_MANAGE_PANEL,
          Permission.GARAGES_VIEW,
        ],
        children: [
          {
            id: 'vehicles-catalogue',
            label: 'Catalogue',
            to: '/vehicles',
            icon: Car,
            permissions: [Permission.VEHICLES_VIEW],
            end: true,
          },
          {
            id: 'vehicles-fuel',
            label: 'Fuel',
            to: '/vehicles/fuel',
            icon: Fuel,
            permissions: [Permission.VEHICLES_VIEW],
          },
          {
            id: 'vehicles-stations',
            label: 'Gas stations',
            to: '/vehicles/stations',
            icon: Warehouse,
            permissions: [Permission.VEHICLES_VIEW],
          },
          {
            id: 'vehicles-workshops',
            label: 'Workshops',
            to: '/vehicles/workshops',
            icon: Wrench,
            permissions: [Permission.VEHICLES_VIEW],
          },
          {
            id: 'vehicles-dealerships',
            label: 'Dealerships',
            to: '/vehicles/dealerships',
            icon: Store,
            permissions: [Permission.VEHICLES_VIEW],
          },
          {
            id: 'vehicles-garages',
            label: 'Garages',
            to: '/vehicles/garages',
            icon: Warehouse,
            permissions: [Permission.GARAGES_VIEW],
          },
          {
            // The city's car rental: hires, courtesy cars, desks and the catalogue.
            id: 'vehicles-rentals',
            label: 'Car rental',
            to: '/rentals',
            icon: KeyRound,
            permissions: [Permission.RENTALS_VIEW],
          },
          {
            // The car washes, their owners and takings, and the city's prices.
            id: 'vehicles-carwash',
            label: 'Car washes',
            to: '/carwash',
            icon: Droplets,
            permissions: [Permission.CARWASH_VIEW],
          },
          {
            // Plates changed at the Motorizzazione or by staff, and the desk's rules.
            id: 'vehicles-plates',
            label: 'Plate changes',
            to: '/plates',
            icon: RectangleHorizontal,
            permissions: [Permission.PLATES_VIEW],
          },
          {
            id: 'vehicles-panel',
            label: 'Panel',
            to: '/vehicles/panel',
            icon: Radio,
            permissions: [Permission.VEHICLES_MANAGE_PANEL],
          },
          {
            id: 'vehicles-setup',
            label: 'Setup',
            to: '/vehicles/setup',
            icon: Wrench,
            permissions: [Permission.VEHICLES_MANAGE],
          },
        ],
      },
    ],
  },
  {
    // The police, the courts and what they enforce.
    id: 'law',
    heading: 'Law & order',
    items: [
      {
        // The records the police and the courts write in game, and the codes they charge,
        // which staff keep.
        id: 'mdt',
        label: 'MDT',
        to: '/mdt',
        icon: Siren,
        permissions: [Permission.MDT_VIEW],
      },
      {
        // The control room the police's calls go through, which staff may watch, or run
        // with the radio.
        id: 'dispatch',
        label: 'Dispatch',
        to: '/dispatch',
        icon: Headset,
        permissions: [Permission.DISPATCH_VIEW, Permission.MDT_MANAGE],
      },
      {
        // Who is inside Bolingbroke, who escaped, and how a sentence is served.
        id: 'jail',
        label: 'Jail',
        to: '/jail',
        icon: LockKeyhole,
        permissions: [Permission.JAIL_VIEW],
      },
      {
        // The robberies under way and every one before, the places that can be robbed
        // with their cooldowns, and the balance staff tune.
        id: 'robberies',
        label: 'Robberies',
        to: '/robberies',
        icon: Vault,
        permissions: [Permission.ROBBERIES_VIEW],
      },
      {
        // The speed cameras by the road and the police's tripods, and every car they
        // caught: the fines themselves live in the MDT.
        id: 'speedcameras',
        label: 'Speed cameras',
        to: '/speedcameras',
        icon: Gauge,
        permissions: [Permission.SPEEDCAMERAS_VIEW],
      },
      {
        // The theory exam's questions, the fees and rules, every exam taken, and the
        // licences, with staff's power to give or take a category.
        id: 'drivingschool',
        label: 'Driving school',
        to: '/drivingschool',
        icon: IdCard,
        permissions: [Permission.DRIVINGSCHOOL_VIEW],
      },
      {
        // Ammu-Nation's counters and what they sell, the weapon licence's quiz and range,
        // the applications staff may decide as the police would, and the gun registry.
        id: 'firearms',
        label: 'Firearms',
        to: '/firearms',
        icon: FileBadge,
        permissions: [Permission.FIREARMS_VIEW],
      },
      {
        // The policies bought from the phone, the claims for cars destroyed or stolen
        // and what came of them, and the three tiers' prices and waits.
        id: 'insurance',
        label: 'Insurance',
        to: '/insurance',
        icon: ShieldCheck,
        permissions: [Permission.INSURANCE_VIEW],
      },
      {
        // The cars the police took and what getting them back costs, the tow trucks'
        // deliveries, the yards and the fees.
        id: 'impound',
        label: 'Impound',
        to: '/impound',
        icon: Truck,
        permissions: [Permission.IMPOUND_VIEW],
      },
    ],
  },
  {
    // What characters carry and the networks it talks over.
    id: 'devices',
    heading: 'Devices & network',
    items: [
      {
        // Who has which number, what they photographed and texted, and the calls ringing
        // now.
        id: 'phones',
        label: 'Phones',
        to: '/phones',
        icon: Smartphone,
        permissions: [Permission.PHONES_VIEW, Permission.PHONES_MEDIA],
        children: [
          {
            id: 'phones-list',
            label: 'Phones',
            to: '/phones',
            icon: Smartphone,
            permissions: [Permission.PHONES_VIEW],
            end: true,
          },
          {
            id: 'phones-calls',
            label: 'Calls',
            to: '/phones/calls',
            icon: PhoneCall,
            permissions: [Permission.PHONES_VIEW],
          },
          {
            id: 'phones-media',
            label: 'Photos & videos',
            to: '/phones/media',
            icon: Images,
            permissions: [Permission.PHONES_MEDIA],
          },
        ],
      },
      {
        // The Slates: whose each is, what its camera took, what is kept on it.
        id: 'tablets',
        label: 'Tablets',
        to: '/tablets',
        icon: Tablet,
        permissions: [Permission.TABLETS_VIEW, Permission.TABLETS_MEDIA],
        children: [
          {
            id: 'tablets-list',
            label: 'Tablets',
            to: '/tablets',
            icon: Tablet,
            permissions: [Permission.TABLETS_VIEW],
            end: true,
          },
          {
            id: 'tablets-media',
            label: 'Photos & videos',
            to: '/tablets/media',
            icon: Images,
            permissions: [Permission.TABLETS_MEDIA],
          },
        ],
      },
      {
        // Every address, the email sold for names, and — behind its own permission,
        // audited — what a mailbox holds.
        id: 'mail',
        label: 'Mail',
        to: '/mail',
        icon: Mail,
        permissions: [Permission.MAIL_VIEW],
        children: [
          {
            id: 'mail-list',
            label: 'Mail',
            to: '/mail',
            icon: Mail,
            permissions: [Permission.MAIL_VIEW],
            end: true,
          },
          {
            id: 'mail-setup',
            label: 'Setup',
            to: '/mail/setup',
            icon: Wrench,
            permissions: [Permission.MAIL_MANAGE],
          },
        ],
      },
      {
        // A name on the city's web is how a shop, a bank or a person is found in the
        // laptop's browser, and this is who holds it.
        id: 'domains',
        label: 'Domains',
        to: '/domains',
        icon: Globe,
        permissions: [Permission.DOMAINS_VIEW],
        children: [
          {
            id: 'domains-registry',
            label: 'Registry',
            to: '/domains',
            icon: Globe,
            permissions: [Permission.DOMAINS_VIEW],
            end: true,
          },
          {
            id: 'domains-hosting',
            label: 'Hosting',
            to: '/domains/hosting',
            icon: Server,
            permissions: [Permission.DOMAINS_VIEW],
          },
          {
            id: 'domains-setup',
            label: 'Setup',
            to: '/domains/setup',
            icon: Wrench,
            permissions: [Permission.DOMAINS_MANAGE],
          },
        ],
      },
      {
        // The phones' signal: the masts staff place in game, on the map.
        id: 'network',
        label: 'Mobile network',
        to: '/network',
        icon: RadioTower,
        permissions: [Permission.NETWORK_VIEW],
      },
      {
        // The city's mains: the substations, the blackouts going on and the past ones.
        id: 'power',
        label: 'Power grid',
        to: '/power',
        icon: Zap,
        permissions: [Permission.POWER_VIEW],
      },
    ],
  },
  {
    // The city's press and social networks.
    id: 'media',
    heading: 'Media',
    items: [
      {
        // The paper the newsroom writes in game, its readers' comments, the ads, the
        // channel's broadcasts (the one on air to watch) and big screens.
        id: 'news',
        label: 'Weazel News',
        to: '/weazel',
        icon: Newspaper,
        permissions: [Permission.NEWS_VIEW],
      },
      {
        // The city's social network. Its accounts and their blue ticks, what they post,
        // their stories and lives (the ones on air to watch).
        id: 'instashot',
        label: 'Instashot',
        to: '/instashot',
        icon: Camera,
        permissions: [Permission.INSTASHOT_VIEW],
      },
      {
        // The city's X. Its accounts with their blue, gold and grey ticks and Premium,
        // what they post, the requests for a badge and the Spaces on air.
        id: 'chirp',
        label: 'Chirp',
        to: '/chirp',
        icon: Bird,
        permissions: [Permission.CHIRP_VIEW],
      },
      {
        // The radio stations of the FM band: their licence keys, the laptops they run
        // on, what is on air, and the DJ gear players put down in the world.
        id: 'stations',
        label: 'Radio stations',
        to: '/stations',
        icon: RadioTower,
        permissions: [Permission.STATIONS_VIEW],
      },
    ],
  },
  {
    id: 'configuration',
    heading: 'Configuration',
    items: [
      {
        id: 'ticket-config',
        label: 'Ticket setup',
        to: '/tickets/categories',
        icon: LayoutGrid,
        permissions: [
          Permission.TICKETS_MANAGE_CATEGORIES,
          Permission.TICKETS_MANAGE_FORMS,
          Permission.TICKETS_MANAGE_PANELS,
        ],
        children: [
          {
            id: 'ticket-categories',
            label: 'Categories',
            to: '/tickets/categories',
            icon: LayoutGrid,
            permissions: [Permission.TICKETS_MANAGE_CATEGORIES],
          },
          {
            id: 'ticket-forms',
            label: 'Forms',
            to: '/tickets/forms',
            icon: FileText,
            permissions: [Permission.TICKETS_MANAGE_FORMS],
          },
          {
            id: 'ticket-panels',
            label: 'Panels',
            to: '/tickets/panels',
            icon: Radio,
            permissions: [Permission.TICKETS_MANAGE_PANELS],
          },
        ],
      },
      {
        id: 'application-positions',
        label: 'Positions',
        to: '/applications/positions',
        icon: BriefcaseBusiness,
        permissions: [Permission.APPLICATIONS_MANAGE],
      },
      {
        id: 'staff-panel',
        label: 'Staff panel',
        to: '/staff-panel',
        icon: Crown,
        permissions: [Permission.STAFF_MANAGE_PANEL],
      },
      {
        id: 'absence-panel',
        label: 'Absence panel',
        to: '/absences/panel',
        icon: CalendarCheck,
        permissions: [Permission.ABSENCES_MANAGE_PANEL],
      },
      {
        id: 'discipline-setup',
        label: 'Discipline setup',
        to: '/discipline/setup',
        icon: Gauge,
        permissions: [Permission.DISCIPLINE_MANAGE],
      },
      {
        id: 'moderation-rules',
        label: 'Moderation rules',
        to: '/moderation/rules',
        icon: MessageSquareWarning,
        permissions: [Permission.MODERATION_MANAGE_RULES],
      },
      {
        id: 'settings',
        label: 'Settings',
        to: '/settings',
        icon: Settings,
        permissions: [Permission.SETTINGS_VIEW],
      },
      {
        id: 'permissions',
        label: 'Roles & permissions',
        to: '/settings/permissions',
        icon: SlidersHorizontal,
        permissions: [Permission.SETTINGS_MANAGE_PERMISSIONS],
      },
    ],
  },
  {
    id: 'system',
    heading: 'System',
    items: [
      {
        id: 'bot',
        label: 'Bot',
        to: '/bot',
        icon: Bot,
        permissions: [Permission.BOT_VIEW],
      },
      {
        id: 'audit',
        label: 'Audit log',
        to: '/audit',
        icon: ScrollText,
        permissions: [Permission.AUDIT_VIEW],
      },
      {
        id: 'activity',
        label: 'Activity',
        to: '/activity',
        icon: Activity,
        permissions: [Permission.DASHBOARD_VIEW],
      },
    ],
  },
]

/** Flatten the tree for the command palette and breadcrumb lookups. */
export function flattenNavigation(): NavItem[] {
  const flat: NavItem[] = []
  for (const group of NAVIGATION) {
    for (const item of group.items) {
      flat.push(item)
      if (item.children) flat.push(...item.children)
    }
  }
  return flat
}
